import { createReport } from "@/api/endpoints/reports";
import { getSearchList } from "@/api/endpoints/searches";
import {
  INTERVALS_FOR_TIME_COMPARISON,
  REPORT_SUBSCRIPTION_INTERVAL,
  REPORT_TEMPLATE_TYPE,
  ReportSubscriptionInterval,
  ReportTemplateType,
} from "@/constants";
import { Button } from "@components/ui/button";
import {
  DialogHeader,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@components/ui/dialog";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@components/ui/field";
import {
  MultiSelect,
  MultiSelectTrigger,
  MultiSelectContent,
  MultiSelectSearch,
  MultiSelectList,
  MultiSelectItem,
  MultiSelectGroup,
  parseMultiSelectValue,
  groupMultiSelectValues,
} from "@components/ui/multi-select";
import { zodResolver } from "@hookform/resolvers/zod";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Controller, SubmitHandler, useFormState } from "react-hook-form";
import { useFormCompat } from "@/hooks/useFormCompat";
import { generatePath, useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";
import z from "zod";
import { getTags } from "@/api/endpoints/tags";
import { useGroupedSearches } from "@/hooks/useGroupedSearches";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@components/ui/select";
import { i18n } from "@lingui/core";
import { CalendarIcon, MessageSquareIcon } from "lucide-react";
import { ROUTES } from "@/routes_new";

type CreateReportDialogProps = {
  open: boolean;
  onClose: () => void;
  template: ReportTemplateType;
};

export default function CreateReportDialog({
  template,
  onClose,
  open,
}: CreateReportDialogProps) {
  const { t } = useLingui();
  const history = useHistory();

  // URL state
  const { orgUuid } = useParams<{ orgUuid: string }>();

  // Server state
  const queryClient = useQueryClient();

  const { data: tags } = useQuery({
    queryKey: [orgUuid, "tags"],
    queryFn: getTags,
  });

  const { data: searches } = useQuery({
    queryKey: [orgUuid, "searches"],
    queryFn: () => getSearchList({ light: true }),
  });

  const { activeSearches, savedSearches, inactiveSearches } =
    useGroupedSearches(searches);

  // Validation
  const formSchema = useFormSchema(template);

  // Form state
  const form = useFormCompat<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      searchesAndTags: [],
    },
  });

  const formState = useFormState({ control: form.control });

  // Helpers
  function handleClose() {
    form.reset();
    onClose();
  }

  // Server state
  const { mutateAsync: mutateCreateReport } = useMutation({
    mutationFn: createReport,
    onSuccess: async (res) => {
      if (!res.ok) {
        throw new Error();
      }

      queryClient.invalidateQueries({
        queryKey: [orgUuid, "reports"],
      });

      handleClose();

      history.push(
        generatePath(ROUTES.REPORTS_EDIT_ORGID, {
          orgUuid,
          uuid: res.data.uuid,
        })
      );
    },
    onError: () => {
      toast.error(t`Misslyckades med att skapa rapporten`);
    },
  });

  // Helpers
  function getLabel(prefixedValue: string) {
    const fallback = t`Hittades inte`;

    const { prefix, value } = parseMultiSelectValue(prefixedValue);

    if (prefix === "search") {
      const search = searches?.ok
        ? searches.data.find((s) => s.uuid === value)
        : null;
      return search?.title || fallback;
    }

    if (prefix === "tag") {
      const tag = tags?.ok ? tags.data.find((t) => t.slug === value) : null;
      return tag?.name || fallback;
    }

    return fallback;
  }

  // Handlers
  const handleSubmit: SubmitHandler<z.infer<typeof formSchema>> = function (
    values
  ) {
    const grouped = groupMultiSelectValues(values.searchesAndTags);

    return mutateCreateReport({
      alert_sources: grouped.search || [],
      tag_sources: grouped.tag || [],
      template,
      interval:
        "interval" in values &&
        template === REPORT_TEMPLATE_TYPE.TIME_COMPARISON
          ? (values.interval as ReportSubscriptionInterval)
          : undefined,
    });
  };

  function renderTitle() {
    switch (template) {
      case REPORT_TEMPLATE_TYPE.COMPARISON:
        return <Trans>Ny jämförelserapport</Trans>;
      case REPORT_TEMPLATE_TYPE.TIME_COMPARISON:
        return <Trans>Ny tidsjämförelserapport</Trans>;
      default:
        return <Trans>Ny överblicksrapport</Trans>;
    }
  }

  function renderDialogDescription() {
    switch (template) {
      case REPORT_TEMPLATE_TYPE.COMPARISON:
        return (
          <Trans>
            Jämför prestanda över varumärken, konkurrenter eller ämnen.
          </Trans>
        );
      case REPORT_TEMPLATE_TYPE.TIME_COMPARISON:
        return (
          <Trans>
            Välj en enskild bevakning, sparat sökresultat eller tagg att
            analysera över tid.
          </Trans>
        );
      default:
        return (
          <Trans>
            Förstå medieexponeringen av ditt varumärke eller din organisation.
          </Trans>
        );
    }
  }

  function renderFieldDescription() {
    switch (template) {
      case REPORT_TEMPLATE_TYPE.COMPARISON:
        return (
          <FieldDescription>
            <Trans>
              Jämförelserapporter kan byggas från bevakningar, sparade
              sökresultat eller taggar.
            </Trans>
          </FieldDescription>
        );
      case REPORT_TEMPLATE_TYPE.TIME_COMPARISON:
        return null;
      default:
        return (
          <FieldDescription>
            <Trans>
              Överblicksrapporter kan byggas från bevakningar, sparade
              sökresultat eller taggar.
            </Trans>
          </FieldDescription>
        );
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="create-report-modal">
        <DialogHeader>
          <DialogTitle>{renderTitle()}</DialogTitle>
          <DialogDescription>{renderDialogDescription()}</DialogDescription>
        </DialogHeader>

        {searches?.ok && tags?.ok ? (
          <form
            id="form-comparison-report"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FieldGroup>
              <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                <Controller
                  name="searchesAndTags"
                  control={form.control}
                  render={({ field, fieldState }) => {
                    return (
                      <Field
                        data-invalid={fieldState.invalid}
                        className="col-span-2"
                      >
                        <MultiSelect
                          value={field.value}
                          onChange={field.onChange}
                          getLabel={getLabel}
                        >
                          <MultiSelectTrigger
                            icon={MessageSquareIcon}
                            id="form-comparison-report-search-ids"
                            placeholder={t`Bevakningar, sparade sökresultat och taggar`}
                          />
                          <MultiSelectContent>
                            <MultiSelectSearch placeholder={t`Sök...`} />
                            <MultiSelectList>
                              <MultiSelectGroup heading={t`Aktiva bevakningar`}>
                                {activeSearches.map((search) => (
                                  <MultiSelectItem
                                    key={search.uuid}
                                    value={search.uuid}
                                    label={search.title}
                                    valuePrefix="search"
                                  />
                                ))}
                              </MultiSelectGroup>
                              <MultiSelectGroup
                                heading={t`Inaktiva bevakningar`}
                              >
                                {inactiveSearches.map((search) => (
                                  <MultiSelectItem
                                    key={search.uuid}
                                    value={search.uuid}
                                    label={search.title}
                                    valuePrefix="search"
                                  />
                                ))}
                              </MultiSelectGroup>
                              <MultiSelectGroup
                                heading={t`Sparade sökresultat`}
                              >
                                {savedSearches.map((search) => (
                                  <MultiSelectItem
                                    key={search.uuid}
                                    value={search.uuid}
                                    label={search.title}
                                    valuePrefix="search"
                                  />
                                ))}
                              </MultiSelectGroup>
                              <MultiSelectGroup heading={t`Taggar`}>
                                {tags.data
                                  .filter((tag) => tag.slug !== "favorited")
                                  .map((tag) => (
                                    <MultiSelectItem
                                      key={tag.slug}
                                      value={tag.slug}
                                      label={tag.name}
                                      valuePrefix="tag"
                                    />
                                  ))}
                              </MultiSelectGroup>
                            </MultiSelectList>
                          </MultiSelectContent>
                        </MultiSelect>

                        {renderFieldDescription()}

                        {fieldState.invalid && (
                          <FieldError errors={[fieldState.error]} />
                        )}
                      </Field>
                    );
                  }}
                />

                {template === REPORT_TEMPLATE_TYPE.TIME_COMPARISON && (
                  <Controller
                    name="interval"
                    control={form.control}
                    render={({ field, fieldState }) => {
                      return (
                        <Field
                          className="col-span-2"
                          data-invalid={fieldState.invalid}
                        >
                          <FieldLabel htmlFor="interval">{t`Intervall`}</FieldLabel>
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                          >
                            <SelectTrigger>
                              <div className="flex items-center gap-2">
                                <CalendarIcon className="size-4" />
                                <SelectValue>
                                  {(interval: string | null) => {
                                    const label =
                                      interval &&
                                      INTERVALS_FOR_TIME_COMPARISON[
                                        interval as keyof typeof INTERVALS_FOR_TIME_COMPARISON
                                      ];

                                    return label
                                      ? i18n._(label)
                                      : t`Välj ett intervall`;
                                  }}
                                </SelectValue>
                              </div>
                            </SelectTrigger>
                            <SelectContent>
                              {Object.entries(
                                INTERVALS_FOR_TIME_COMPARISON
                              ).map(([value, label]) => (
                                <SelectItem key={value} value={value}>
                                  {i18n._(label)}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {fieldState.invalid && (
                            <FieldError errors={[fieldState.error]} />
                          )}
                        </Field>
                      );
                    }}
                  />
                )}
              </div>
            </FieldGroup>
          </form>
        ) : null}

        <DialogFooter>
          <Button
            variant="outline"
            type="button"
            onClick={handleClose}
            disabled={formState.isSubmitting}
          >
            <Trans>Avbryt</Trans>
          </Button>
          <Button
            type="submit"
            form="form-comparison-report"
            disabled={formState.isSubmitting}
          >
            <Trans>Skapa rapport</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useFormSchema(template: ReportTemplateType) {
  const { t } = useLingui();

  switch (template) {
    case REPORT_TEMPLATE_TYPE.COMPARISON:
      return z.object({
        searchesAndTags: z.array(z.string()).min(2, {
          message: t`Välj minst 2 bevakningar`,
        }),
        interval: z.undefined().optional(),
      });
    case REPORT_TEMPLATE_TYPE.TIME_COMPARISON:
      return z.object({
        searchesAndTags: z
          .array(z.string())
          .min(1, {
            message: t`Välj 1 bevakning, sparat sökresultat eller tagg`,
          })
          .max(1, {
            message: t`Välj 1 bevakning, sparat sökresultat eller tagg`,
          }),
        interval: z.enum(Object.values(REPORT_SUBSCRIPTION_INTERVAL), {
          message: t`Välj ett intervall för din rapport`,
        }),
      });
    default:
      return z.object({
        searchesAndTags: z.array(z.string()).min(1, {
          message: t`Välj minst en bevakning`,
        }),
        interval: z.undefined().optional(),
      });
  }
}
